import React, { useState, useMemo, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop, get } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import SharedBudgetSwitchView from '@bingads-webui-campaign-legacy/shared-budget/view/shared-budget-switch-editor';
import Budget from '@bingads-webui-campaign-legacy/budget/view/budget';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidatedLegacyBidStrategy } from '@bingads-webui-campaign/bid-strategy';
import types from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import { getBudgetStyles } from '../styles/bid-strategy-styles';

const AudienceBudgetBidStrategyInternal = ({
  data,
  classes,
  updateValidation,
  campaignType,
  campaignSubType,
  isEnableMSANDisplayCampaignsEnabled,
  isEnableMSANVideoCampaignsEnabled,
  setBudgetView,
  hideBidStrategy,
  activity,
}) => {
  const i18n = useContext(I18nContext);
  const campaign = useMemo(() => (
    isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled ? data : data.campaign
  ), [data, isEnableMSANDisplayCampaignsEnabled, isEnableMSANVideoCampaignsEnabled]);
  const bidStrategyName = get(data, ['campaign', 'BidStrategyName'], '');

  const [sharedBudgetRef, setSharedBudgetRef] = useState();

  const sharedBudget = useMemo(() => {
    const factory = SharedBudgetSwitchView.newFactory(Budget);

    const sharedBudgetView = factory({
      accountCurrencyCode: 'USD',
      model: undefined,
      withRealTimeUpdates: true,
    });

    sharedBudgetView.render();

    return sharedBudgetView;
  }, []);

  const onBudgetChange = () => {
    const budget = sharedBudget.getValues();
    const isValid = sharedBudget.validate();
    if (activity) {
      activity.signal(`onBudgetChange - ${JSON.stringify(budget)} - isValid: ${isValid}}`);
    }
    updateValidation(isValid ? [] : [i18n.getString(_TL_('Invalid budget'))], { BudgetId: budget && budget.Id, Budget: { Amount: budget && budget.Amount, Id: budget && budget.Id, Type: budget && budget.Type } });
  };

  useEffect(() => {
    if (setBudgetView && sharedBudget) {
      setBudgetView(sharedBudget);
    }
  }, [sharedBudget, setBudgetView]);

  useEffect(() => {
    if (sharedBudgetRef) {
      sharedBudgetRef.innerHTML = '';
      sharedBudgetRef.append(sharedBudget.el);

      sharedBudget.update(campaign.Budget);
      sharedBudget.budgetView.on('budget.updated', onBudgetChange);
      sharedBudget.budgetView.on('change', onBudgetChange);
      sharedBudget.grid.gridView.on('didUpdate', onBudgetChange);
      sharedBudget.viewModel.sharedBudgetSwitch.subscribe(() => {
        onBudgetChange();
      });
    }

    return noop;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedBudgetRef]);

  useEffect(() => {
    sharedBudget.viewModel.isCPSSelected(campaign.BiddingScheme.Type === types.costPerSale);
    sharedBudget.viewModel.sharedBudgetIsNotAllowedWarning(i18n.getString(_TL_('Shared budget is not allowed if CPS is selected')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.BiddingScheme.Type]);

  return (
    <div className={classNames(classes.pickerContainer, 'budget-bid-strategy-container')} >
      <div className=".campaign-settings__container vanila-budget">
        <div className={classNames('budget-view')} ref={newRef => setSharedBudgetRef(newRef)} />
      </div>
      {!hideBidStrategy &&
        <div>
          <ValidatedLegacyBidStrategy
            className="vanila-bid-strategy"
            campaignId={campaign.Id}
            campaignType={campaignType}
            hideBudgetMinRecommendation
            campaignSubType={campaignSubType}
            validationProps={{
            initialValue: campaign.BiddingScheme,
            data: campaign,
          }}
            isCreation
            bidStrategyName={bidStrategyName}
            i18nNewDesign={i18n}
            validateWithBudget
            budgetAmount={sharedBudget && sharedBudget.getValues() ? sharedBudget.getValues().Amount : null}
          />
        </div>
      }
    </div>
  );
};

AudienceBudgetBidStrategyInternal.protoTypes = {
  classes: PropTypes.shape({
    pickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Budget: PropTypes.shape({
        Amount: PropTypes.number,
        Id: PropTypes.number,
        Type: PropTypes.string,
      }),
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
        BidStrategyId: PropTypes.number,
      }),
      BidStrategyName: PropTypes.string,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  campaignType: PropTypes.string.isRequired,
  campaignSubType: PropTypes.string,
  isEnableMSANDisplayCampaignsEnabled: PropTypes.bool,
  isEnableMSANVideoCampaignsEnabled: PropTypes.bool,
  setBudgetView: PropTypes.func,
  hideBidStrategy: PropTypes.bool,
  activity: PropTypes.shape({
    signal: PropTypes.func,
  }),
};

AudienceBudgetBidStrategyInternal.defaultProps = {
  campaignSubType: undefined,
  isEnableMSANDisplayCampaignsEnabled: false,
  isEnableMSANVideoCampaignsEnabled: false,
  setBudgetView: undefined,
  hideBidStrategy: false,
  activity: undefined,
};

export const AudienceBudgetBidStrategy = withCustomValidation(withDefaultStyles(AudienceBudgetBidStrategyInternal, getBudgetStyles));
